import {
  EIpRateLimitIdentity,
  ICommonSettings,
  IIpRateLimitingSetting
} from "@kuika/kuika-cl-model/src/models/common-config";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigTooltip } from "../app-settings-config-tooltip";
import { ConfigModalBody, ConfigModalFooter, ConfigModalGroupHeading } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";
import { RulePeriodInput } from "./app-settings-config-rate-limit-period";
import "./app-settings-config-ip-rate-limiting.scss";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const IP_RATE_LIMITING_SETTINGS_SEARCH_TERMS: string[] = [
  "Enable rate limiting",
  "Rate limit by",
  "General rules",
  "Period",
  "Limit"
];

const PERIOD_PATTERN = /^\d+\s*(s|m|h|d)$/i;

export interface IConfigIpRateLimitingSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingSetting?: IIpRateLimitingSetting;
}

export interface IConfigIpRateLimitingSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingIpRateLimitingId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigIpRateLimitingSettings extends React.PureComponent<
  IConfigIpRateLimitingSettingsProps,
  IConfigIpRateLimitingSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigIpRateLimitingSettingsProps) {
    super(props);
    this.state = { saveStatus: "idle", deleteStatus: "idle", editingSetting: undefined };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigIpRateLimitingSettingsProps & IWithSDProps) {
    if (prevProps.editingIpRateLimitingId && !this.props.editingIpRateLimitingId) {
      this.formRef.current?.resetFields();
    }

    if (prevProps.editingIpRateLimitingId !== this.props.editingIpRateLimitingId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
      return;
    }

    if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  private getDefaultRules() {
    return [
      { period: "10s", limit: 250000000 },
      { period: "1m", limit: 250000000 },
      { period: "1h", limit: 250000000 }
    ];
  }

  loadSetting = () => {
    if (!this.props.editingIpRateLimitingId) {
      this.setState({ editingSetting: undefined }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue({
          enabled: false,
          identity: EIpRateLimitIdentity.ClientId,
          generalRules: this.getDefaultRules()
        });
      });
      return;
    }

    const list = _.cloneDeep(this.props.commonSettings?.ipRateLimitingSettings) || [];
    const editingSetting = list.find((s) => s.id === this.props.editingIpRateLimitingId);
    if (!editingSetting) {
      this.setState({ editingSetting: undefined }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue({
          enabled: false,
          identity: EIpRateLimitIdentity.ClientId,
          generalRules: this.getDefaultRules()
        });
      });
      return;
    }

    this.setState({ editingSetting }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue({
        ...editingSetting,
        enabled: editingSetting.enabled ?? false,
        identity: editingSetting.identity ?? EIpRateLimitIdentity.ClientId,
        generalRules: (editingSetting.generalRules?.length ? editingSetting.generalRules : this.getDefaultRules()).map(
          (x) => ({ period: x.period, limit: x.limit })
        )
      });
    });
  };

  existingNameValidator = async (_rule, value) => {
    const list = this.props.commonSettings?.ipRateLimitingSettings || [];
    const isDuplicate =
      list.some((x) => x.name === value && x.id !== this.props.editingIpRateLimitingId) || value === "Default";

    if (isDuplicate) throw new Error("Rate Limiting Setting Name already exists.");
  };

  onFinish = async (data: any) => {
    this.saveStatus.start();

    const enabled = !!data.enabled;

    const obj: IIpRateLimitingSetting = {
      id:
        this.props.editingIpRateLimitingId && this.props.editingIpRateLimitingId !== ""
          ? this.props.editingIpRateLimitingId
          : Guid.create().toString(),
      name: data.name,
      enabled,
      identity: data.identity,
      generalRules: enabled
        ? (data.generalRules || []).map((r) => ({
            period: (r?.period || "").trim(),
            limit: Number(r?.limit ?? 0)
          }))
        : data.generalRules || []
    };

    const saveParamsBase = {
      formInputName: this.props.formInputName,
      config: obj,
      configKey: "ipRateLimitingSettings" as const,
      onError: () => this.saveStatus.fail(),
      onFinal: () => {}
    };

    if (this.props.editingIpRateLimitingId) {
      this.props.handleUpdateCommonConfig({
        ...saveParamsBase,
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        }
      });
      return;
    }

    this.props.handleCreateCommonConfig({
      ...saveParamsBase,
      onSuccess: () => {
        this.saveStatus.succeed(() => {
          this.props.closeModal();
        });
      }
    });
  };

  deleteSetting = () => {
    if (!this.props.editingIpRateLimitingId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingIpRateLimitingId || "",
      configKey: "ipRateLimitingSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification("Rate limiting setting has been deleted");
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Rate limiting setting"
        secondHeader="Rate limiting setting"
        width={720}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingIpRateLimitingId ? this.deleteSetting : undefined}
            deleteTestId="ipRateLimitingSettingDeleteButton"
            submitTestId="ipRateLimitingSettingCreateButton"
            submitLabel={this.props.editingIpRateLimitingId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configIpRateLimitingForm"
          />
        }
      >
        <Form
          id="configIpRateLimitingForm"
          name="ipRateLimitingSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("appsettings_ipratelimiting_description")}>
            <ConfigField label="Name" required tooltipContentKey="appsettings_ipratelimiting_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Rate Limiting Setting name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Rate Limiting Setting name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="ipRateLimitingSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Enable rate limiting" tooltipContentKey="appsettings_ipratelimiting_enabled">
              <Form.Item name="enabled" valuePropName="checked" initialValue={false}>
                <KuikaSwitch dataTestId="ipRateLimitingSettingEnabledSwitch" />
              </Form.Item>
            </ConfigField>

            <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.enabled !== cur?.enabled}>
              {({ getFieldValue }) => {
                const enabled = !!getFieldValue("enabled");

                return (
                  <>
                    <ConfigField label="Rate limit by" required tooltipContentKey="appsettings_ipratelimiting_identity">
                      <Form.Item
                        name="identity"
                        rules={enabled ? [{ required: true, message: "Please choose rate limit identity type." }] : []}
                      >
                        <KuikaSelectbox
                          size="md"
                          data-testid="ipRateLimitingSettingIdentityDropdown"
                          disabled={!enabled}
                          options={[
                            { value: EIpRateLimitIdentity.Ip, label: "IP" },
                            { value: EIpRateLimitIdentity.ClientId, label: "Client-ID" }
                          ]}
                        />
                      </Form.Item>
                    </ConfigField>
                    <ConfigModalGroupHeading>
                      General rules
                      <ConfigTooltip contentKey="appsettings_ipratelimiting_generalrules" />
                    </ConfigModalGroupHeading>
                    <Form.List
                      name="generalRules"
                      rules={
                        enabled
                          ? [
                              {
                                validator: async (_, rules) => {
                                  if (!rules || rules.length === 0) throw new Error("Please add at least one rule.");
                                }
                              }
                            ]
                          : []
                      }
                    >
                      {(fields, { add, remove }, { errors }) => (
                        <div className="kuika-rate-limit-rules">
                          <div className="kuika-rate-limit-rules__head">
                            <span className="kuika-rate-limit-rules__heading">
                              Period
                              {enabled && <span className="kuika-config-field__required">*</span>}
                            </span>
                            <span className="kuika-rate-limit-rules__heading">
                              Limit
                              {enabled && <span className="kuika-config-field__required">*</span>}
                            </span>
                            <span />
                          </div>

                          {fields.map((field) => (
                            <div key={field.key} className="kuika-rate-limit-rules__row">
                              <Form.Item
                                {...field}
                                name={[field.name, "period"]}
                                rules={
                                  enabled
                                    ? [
                                        { required: true, message: "Period is required." },
                                        {
                                          validator: async (_r, v) => {
                                            const val = String(v ?? "").trim();
                                            // `required` above owns the empty case. Without this
                                            // both rules fire and an emptied box gets two messages
                                            // saying the same thing.
                                            if (val === "") return;
                                            if (!PERIOD_PATTERN.test(val))
                                              throw new Error("Invalid period (e.g. 10s, 1m, 1h).");
                                          }
                                        }
                                      ]
                                    : []
                                }
                              >
                                <RulePeriodInput disabled={!enabled} />
                              </Form.Item>

                              <Form.Item
                                {...field}
                                name={[field.name, "limit"]}
                                rules={
                                  enabled
                                    ? [
                                        { required: true, message: "Limit is required." },
                                        {
                                          validator: async (_r, v) => {
                                            const raw = String(v ?? "").trim();
                                            if (raw === "") return;
                                            if (!/^\d+$/.test(raw))
                                              throw new Error("Limit must be a positive integer.");
                                            const num = Number(raw);
                                            if (!Number.isFinite(num) || num <= 0)
                                              throw new Error("Limit must be > 0.");
                                          }
                                        }
                                      ]
                                    : []
                                }
                              >
                                <KuikaInput size="md" disabled={!enabled} inputMode="numeric" placeholder="1" />
                              </Form.Item>

                              {/* An icon, not a word: three grey REMOVE buttons down the
                                    right of the table read as the disabled state of the
                                    table rather than as its controls. */}
                              <button
                                type="button"
                                className="kuika-rate-limit-rules__remove"
                                aria-label="Remove rule"
                                disabled={!enabled || fields.length === 1}
                                onClick={(): void => remove(field.name)}
                              >
                                <KuikaSvg name="x" width={16} height={16} />
                              </button>
                            </div>
                          ))}

                          <KuikaButton
                            type="text"
                            size="sm"
                            uppercase
                            disabled={!enabled}
                            onClick={(): void => add({ period: "1m", limit: 250000000 })}
                          >
                            Add rule
                          </KuikaButton>
                          <Form.ErrorList errors={errors} />
                        </div>
                      )}
                    </Form.List>
                  </>
                );
              }}
            </Form.Item>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
